import React, { useContext, useEffect } from 'react';
import { Box, Flex } from 'rebass';
import { ButtonStyled, TextStyled } from '@nike/nike-design-system-components';
import { connect } from 'react-redux';
import { object, func, array, string, bool } from 'prop-types';

import { TableServerSideContext } from '~COMPONENTS/tableServerSide/TableServerSideContext';
import { CatalogEditOptionsModalEllipsis } from './CatalogEditOptionsModalEllipsis';
import {
  addCatalogProducts,
  generateValidationReport,
  removeCatalogProduct,
  removeAllCatalogProducts
} from '../../catalogs/actions';
import { getCatalogOrderTypes, getCatalogStatus } from '../../catalogs/selectors';

import { getCatalogProductsListColumns } from './columns';

import { ActionItemList } from '../../common/ActionItemList';

import { showToast } from '../../../components/toast/actions';

import TableServerSide from '../../../components/tableServerSide/TableServerSide';

import { getPendingStatus } from '../../../util/requestReducer';

import { getAdminHeaderOpenState } from '../../selectors';

import DownloadProducts from '../downloadCatalogProducts/DownloadProducts';

import useModal from '../../../hooks/useModal';

const CatalogProductsList = ({
  currentCatalog,
  catalogId,
  addProductsAction,
  orderTypes,
  removeCatalogProduct,
  isAdminHeaderOpen
}) => {
  const {
    tableInstance: { selectedRowIds = {}, selectedFlatRows, items: catalogProducts },
    totalResources,
    refreshTableData,
    isAllRowsSelected,
    filterQueryParams,
    handleClearSortAndFilters
  } = useContext(TableServerSideContext);

  useEffect(() => {
    refreshTableData();
  }, [isAdminHeaderOpen]);

  const [isActionItemModalOpen, openActionItemModal, closeActionItemModal] = useModal();
  const [isDownloadProductsModalOpen, openDownloadProductsModal, closeDownloadProductsModal] = useModal();

  const orderTypesSet = new Set(orderTypes);
  const isDualOrderType =
    orderTypesSet.has('ATONCE') && (orderTypesSet.has('FORECAST') || orderTypesSet.has('FUTURES'));

  const selectedProductCodes = selectedFlatRows?.map(item => item.original.productCode).join(',');

  const actionItems = {
    Download: { action: () => openDownloadProductsModal() },
    '|': { action: () => {} },
    Delete: {
      modal: {
        actions: [
          { actionName: 'Cancel', action: closeActionItemModal },
          {
            actionName: 'Delete',
            action:
              ({ toggleIsLoading }) =>
              async () => {
                toggleIsLoading(true, 'Deleting Products...');
                if (isAllRowsSelected) {
                  await removeAllCatalogProducts(catalogId, filterQueryParams);
                } else {
                  const requests = selectedFlatRows.map(row =>
                    removeCatalogProduct({ catalogId, productCode: row?.original?.productCode })
                  );
                  await Promise.all(requests);
                }
                toggleIsLoading(false);
                refreshTableData();
                closeActionItemModal();
                showToast({ message: 'Products deleted', type: 'success' }, 5500);
              }
          }
        ],
        content: () => 'Are you sure you want to delete selected product codes?',
        title: 'Confirm Deletion'
      }
    }
  };

  return (
    <Box height='100%' width='100%'>
      <Flex justifyContent='space-between' width='100%' data-testid='product-count' mt='10px'>
        <Flex alignItems='end'>
          <TextStyled appearance='body2' style={{ marginLeft: '5px', marginRight: '10px' }}>
            {totalResources} product{totalResources !== 1 && 's'}
          </TextStyled>
          <ButtonStyled
            appearance='text'
            background='light'
            ariaLabel='Clear All Filters'
            type='button'
            onClick={handleClearSortAndFilters}
            style={{ marginRight: '10px' }}
          >
            Clear All Filters
          </ButtonStyled>
          <ActionItemList
            selected={selectedRowIds}
            actionItems={actionItems}
            isModalOpen={isActionItemModalOpen}
            openModal={openActionItemModal}
            closeModal={closeActionItemModal}
          />
        </Flex>

        <Flex mr='15px'>
          {catalogProducts?.some?.(product => product.invalidReasons?.includes('INACTIVE_LIFECYCLE')) && (
            <TextStyled appearance='body2' style={{ marginRight: '20px' }}>
              {isDualOrderType && 'Product with INACTIVE_LIFECYCLE error will be indexed for AtOnce only'}
            </TextStyled>
          )}
          <CatalogEditOptionsModalEllipsis
            catalog={currentCatalog}
            catalogProducts={catalogProducts}
            addProductsAction={addProductsAction}
          />
        </Flex>
      </Flex>

      <TableServerSide
        columns={getCatalogProductsListColumns()}
        url={`v1/catalogs/${catalogId}/products`}
        includeRowSelect
        fullWidth
        fullHeight
      />
      <DownloadProducts
        closeDownloadProductsModal={closeDownloadProductsModal}
        isDownloadProductsModalOpen={isDownloadProductsModalOpen}
        catalogId={catalogId}
        selectedFlatRows={selectedFlatRows}
        isAllRowsSelected={isAllRowsSelected}
        filterQueryParams={filterQueryParams}
        isCatalogsList={false}
        selectedProductCodes={selectedProductCodes}
      />
    </Box>
  );
};

CatalogProductsList.propTypes = {
  currentCatalog: object.isRequired,
  catalogId: string.isRequired,
  addProductsAction: func.isRequired,
  orderTypes: array.isRequired,
  isAdminHeaderOpen: bool,
  removeCatalogProduct: func.isRequired
};

CatalogProductsList.defaultProps = {
  isAdminHeaderOpen: false
};

const mapStateToProps = (state, { catalogId }) => ({
  isDeletingCatalogProduct: getPendingStatus(state, removeCatalogProduct.event.name),
  isAddingCatalogProduct: getPendingStatus(state, addCatalogProducts.event.name),
  orderTypes: getCatalogOrderTypes(state, catalogId),
  status: getCatalogStatus(state, catalogId),
  isAdminHeaderOpen: getAdminHeaderOpenState(state)
});

const mapDispatchToProps = (dispatch, { catalogId }) => ({
  addProductsAction: products => dispatch(addCatalogProducts({ catalogId, products })),
  removeCatalogProduct: params => dispatch(removeCatalogProduct(params)),
  generateValidationReport: params => dispatch(generateValidationReport(params)),
  showToast: params => dispatch(showToast(params))
});

export default connect(mapStateToProps, mapDispatchToProps)(CatalogProductsList);
